import {ShopStreamItemV2_HighlightsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {
  FONT_SIZES,
  FONT_WEIGHTS,
  HorizontalSlider,
  SliderWrapper,
  Text,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {BrandHighlightsZoomSlider} from './BrandHighlights/BrandHighlightsZoomSlider';
import {useHighlightSlideOpenedState} from './BrandHighlights/useHighlightSlideOpenedState';
import {BrandPageHighlightCard} from './BrandPageHighlightCard';

export const BrandPageHighlights: React.FC<{
  highlightsSection: ShopStreamItemV2_HighlightsSection;
  hideSideButton?: boolean;
  streamPosition: number;
}> = ({highlightsSection, hideSideButton, streamPosition}) => {
  const {isOpen, open, hide, parameter} = useHighlightSlideOpenedState({
    rowIndex: streamPosition,
  });

  const currentSlide = parameter ? Number(parameter) : undefined;

  if (!highlightsSection.highlights.length) {
    return null;
  }

  const openHighlight = (index: number) => {
    open({parameter: `${index}`, replaceHistory: true});
  };

  return (
    <Wrapper
      data-show-title-on-the-left={highlightsSection.highlights.length <= 2}
      data-centered={highlightsSection.highlights.length <= 2}
    >
      <Title as="h2">
        <Subline color="Grey1" transform="uppercase">
          {highlightsSection.subline}
        </Subline>
        <Headline>{highlightsSection.headline}</Headline>
      </Title>
      <StyledHorizontalSlider
        disableHeaderControl
        constrained
        slideCount={2.3}
        slideCountMedium={4}
        hideSideButtons={hideSideButton}
        navButtonsZIndex={1}
      >
        {highlightsSection.highlights.map((highlight, index) => (
          <BrandPageHighlightCard
            key={highlight.highlightId}
            streamPosition={streamPosition}
            highlight={highlight}
            targetUrl={highlight.slides[0]?.cta?.link.url}
            onClick={() => open({parameter: index.toString()})}
          />
        ))}
      </StyledHorizontalSlider>

      <BrandHighlightsZoomSlider
        highlights={highlightsSection.highlights}
        isOpen={isOpen}
        hide={hide}
        openHighlight={openHighlight}
        currentSlide={currentSlide}
        disableOverscroll={hideSideButton}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 30px 0 48px 0;

  &[data-show-title-on-the-left='true'] {
    ${tabletAndUpLinaria(`
      flex-direction: row;
      flex: 0 0 24%;
      gap: 40px;
    `)};
  }
`;

const Title = styled(Text)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;

  ${tabletAndUpLinaria(`
    margin-bottom: 24px;
    align-items: flex-start;
  `)};
`;

const Subline = styled(Text)`
  letter-spacing: 0.5px;
  line-height: 1.6;
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  margin: 0;

  ${tabletAndUpLinaria(`
      line-height: 1.5;
      font-size: ${FONT_SIZES.px12};
  `)}
`;

const Headline = styled(Text)`
  line-height: 1.5;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  margin: 0;

  ${tabletAndUpLinaria(`
      line-height: 1.25;
      font-size: ${FONT_SIZES.px32};
      font-weight: ${FONT_WEIGHTS.heavy};
  `)};
`;

const StyledHorizontalSlider = styled(HorizontalSlider)`
  ${SliderWrapper} {
    & > *:last-child {
      margin-right: 0;
    }

    [data-centered='true'] & {
      ${mobileOnlyLinaria(`justify-content: center;`)}
    }
  }
`;
